import React, { useCallback, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Button from '../Button';
import Typography from '../Typography';
import CalendarIcon from '../Icons/CalendarIcon';
import ChevronDownIcon from '../Icons/ChevronDownIcon';
import CloseIcon from '../Icons/CloseIcon';
import DatePickerPopover from './DatePickerPopover';
import { formatStoredDate, parseStoredDate } from './datePickerValue';
import './DatePickerInput.scss';

function cx(...parts) {
  return parts.filter(Boolean).join(' ');
}

export default function DatePickerInput({
  id,
  value = '',
  onChange,
  disabled = false,
  placeholder,
  error = false,
  allowFutureDate = false,
  className = '',
  overlayClassName = '',
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const fieldId = id || 'ds-date-picker-input';

  const displayPlaceholder =
    placeholder ??
    intl.formatMessage({
      id: 'select-date',
      defaultMessage: 'Select Date',
    });

  const displayText = useMemo(() => {
    const parsed = parseStoredDate(value);
    if (!parsed.isValid()) {
      return null;
    }
    return formatStoredDate(parsed);
  }, [value]);

  const maxDate = allowFutureDate ? null : moment().endOf('day');

  const handleApply = useCallback(
    (nextDate) => {
      onChange?.(formatStoredDate(nextDate));
      setOpen(false);
    },
    [onChange]
  );

  const handleClear = useCallback(
    (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      onChange?.('');
      setOpen(false);
    },
    [onChange]
  );

  const ariaLabel = intl.formatMessage(
    {
      id: 'client-connect-date-picker-aria',
      defaultMessage: 'Date: {value}. Open date picker',
    },
    { value: displayText?.trim() || displayPlaceholder }
  );

  const triggerClassName = cx(
    'ds-date-picker-input__trigger',
    error && 'ds-date-picker-input__trigger--error',
    open && 'ds-date-picker-input__trigger--open'
  );

  const triggerContent = (
    <>
      <CalendarIcon size={20} aria-hidden />
      <Typography
        component="span"
        variant="bodyMedium"
        className={cx(
          'ds-date-picker-input__trigger-text',
          !displayText && 'ds-date-picker-input__trigger-text--placeholder'
        )}
      >
        {displayText ?? displayPlaceholder}
      </Typography>
      <ChevronDownIcon
        size={16}
        className={cx('ds-date-picker-input__chevron', open && 'ds-date-picker-input__chevron--open')}
        aria-hidden
      />
    </>
  );

  return (
    <div className={cx('ds-date-picker-input', open && 'ds-date-picker-input--open', className)}>
      <div
        ref={anchorRef}
        className={cx(
          'ds-date-picker-input__shell',
          error && 'ds-date-picker-input__shell--error',
          displayText && !disabled && 'ds-date-picker-input__shell--has-value'
        )}
      >
        {disabled ? (
          <div
            className={cx('ds-date-picker-input__trigger', 'ds-date-picker-input__trigger--disabled')}
            aria-disabled="true"
            role="button"
            tabIndex={-1}
          >
            {triggerContent}
          </div>
        ) : (
          <Button
            id={`${fieldId}-trigger`}
            variant="white"
            size="base"
            fullWidth
            className={triggerClassName}
            onClick={() => setOpen(true)}
            aria-label={ariaLabel}
            aria-expanded={open}
            aria-haspopup="dialog"
            aria-invalid={error || undefined}
          >
            {triggerContent}
          </Button>
        )}
        {displayText && !disabled ? (
          <button
            type="button"
            className="ds-date-picker-input__clear"
            aria-label={intl.formatMessage({
              id: 'ds-date-picker-clear',
              defaultMessage: 'Clear date',
            })}
            onClick={handleClear}
          >
            <CloseIcon size={16} aria-hidden />
          </button>
        ) : null}
      </div>

      <DatePickerPopover
        open={open}
        onDismiss={() => setOpen(false)}
        onApply={handleApply}
        onClear={displayText ? handleClear : undefined}
        value={value}
        maxDate={maxDate}
        anchorRef={anchorRef}
        overlayClassName={overlayClassName}
      />
    </div>
  );
}

DatePickerInput.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  allowFutureDate: PropTypes.bool,
  className: PropTypes.string,
  overlayClassName: PropTypes.string,
};

DatePickerInput.defaultProps = {
  id: undefined,
  value: '',
  onChange: undefined,
  disabled: false,
  placeholder: undefined,
  error: false,
  allowFutureDate: false,
  className: '',
  overlayClassName: '',
};
